import React, { createContext, useContext, useState, useCallback } from 'react';
import apiCallback, { ApiCallbackResponse } from '../services/apiCallback';

interface IApiCallbackContext {
  callbackIds: string[];
  setCallbackIds: (data: string[]) => void;
  results: Record<string, ApiCallbackResponse | null>;
  setResults: (data: Record<string, ApiCallbackResponse | null>) => void;
  getCallback: (id: string) => Promise<ApiCallbackResponse>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;
}

export const ApiCallbackContext = createContext<IApiCallbackContext>({
  callbackIds: [],
  setCallbackIds: () => {},
  results: {},
  setResults: () => {},
  getCallback: async () => ({ success: false, data: { externalId: '', callbacks: [] } }),
  loading: {},
  error: {},
});

const ApiCallbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [callbackIds, setCallbackIdsState] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, ApiCallbackResponse | null>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<Record<string, string | null>>({});

  // Wrapper para setCallbackIds que limpa os resultados anteriores quando novos IDs são definidos
  const setCallbackIds = useCallback((ids: string[]) => {
    // Limpa resultados anteriores quando novos IDs são definidos
    setResults({});
    setError({});
    setLoading({});
    setCallbackIdsState(ids);
  }, []);

  const getCallback = useCallback(async (id: string): Promise<ApiCallbackResponse> => {
    try {
      setLoading((prev) => ({ ...prev, [id]: true }));
      setError((prev) => ({ ...prev, [id]: null }));
      const response = await apiCallback.getCallback(id);
      setResults((prev) => ({ ...prev, [id]: response }));
      return response;
    } catch (err: any) {
      console.error(`Erro ao buscar callback para ${id}:`, err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar callback';
      setError((prev) => ({ ...prev, [id]: errorMessage }));
      throw new Error(errorMessage);
    } finally {
      setLoading((prev) => ({ ...prev, [id]: false }));
    }
  }, []);

  return (
    <ApiCallbackContext.Provider
      value={{
        callbackIds,
        setCallbackIds,
        results,
        setResults,
        getCallback,
        loading,
        error,
      }}
    >
      {children}
    </ApiCallbackContext.Provider>
  );
};

const useApiCallback = (): IApiCallbackContext => {
  const context = useContext(ApiCallbackContext);
  if (!context) {
    throw new Error('useApiCallback must be used within an ApiCallbackProvider');
  }
  return context;
};

export { ApiCallbackProvider, useApiCallback };

